import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/lib/auth'
import { useTheme } from '@/features/settings/theme'

/**
 * Linear-style command palette.
 *
 * Opens on ⌘K / Ctrl-K from anywhere. Up/Down navigates, Enter
 * executes, Esc / outside-click closes. Filter is a case-insensitive
 * substring against the action's `keywords` field, so "txn",
 * "transactions", "trans" all match the transactions nav.
 *
 * Hand-rolled instead of pulling in kbar/cmdk because the action set
 * is small (under 20) and any third-party palette ships its own
 * styling system we'd have to override. Plus it keeps the initial
 * bundle lean.
 */

interface Action {
  id: string
  label: string
  hint?: string
  keywords: string
  section: 'navigate' | 'create' | 'preferences' | 'account'
  perform: () => void
}

export function CommandPalette() {
  const [open, setOpen] = useState(false)
  const [filter, setFilter] = useState('')
  const [highlight, setHighlight] = useState(0)
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { signOut } = useAuth()
  const { mode, setMode } = useTheme()
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const close = useCallback(() => {
    setOpen(false)
    setFilter('')
    setHighlight(0)
  }, [])

  // Global ⌘K / Ctrl-K. Also closes via the escape handler inside
  // the dialog when open. Skips activation inside input/textarea so
  // shortcuts don't fight with text entry.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null
      const inField =
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.isContentEditable
      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
        // ⌘K from inside an input field is rare enough — allow it
        // (some users hit it accidentally; the palette is still the
        // right thing).
        e.preventDefault()
        setOpen((o) => !o)
        return
      }
      if (e.key === '/' && !inField && !open) {
        // "/" as a quick-open shortcut, like Linear/GitHub. Skipped
        // when an input has focus.
        e.preventDefault()
        setOpen(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  const actions = useMemo<Action[]>(() => {
    const themeOrder = ['system', 'light', 'dark'] as const
    const nextTheme = themeOrder[(themeOrder.indexOf(mode) + 1) % themeOrder.length]
    return [
      {
        id: 'nav-budget',
        label: t('nav.budget'),
        keywords: 'budget envelope ynab',
        section: 'navigate',
        perform: () => void navigate({ to: '/budget' }),
      },
      {
        id: 'nav-transactions',
        label: t('nav.transactions'),
        keywords: 'transactions txns ledger',
        section: 'navigate',
        perform: () => void navigate({ to: '/transactions' }),
      },
      {
        id: 'nav-accounts',
        label: t('nav.accounts'),
        keywords: 'accounts banks plaid',
        section: 'navigate',
        perform: () => void navigate({ to: '/accounts' }),
      },
      {
        id: 'nav-reports',
        label: t('nav.reports'),
        keywords: 'reports analytics charts',
        section: 'navigate',
        perform: () => void navigate({ to: '/reports' }),
      },
      {
        id: 'nav-goals',
        label: t('nav.goals'),
        keywords: 'goals targets savings',
        section: 'navigate',
        perform: () => void navigate({ to: '/goals' }),
      },
      {
        id: 'nav-coach',
        label: t('nav.coach'),
        keywords: 'coach ai chat help',
        section: 'navigate',
        perform: () => void navigate({ to: '/coach' }),
      },
      {
        id: 'nav-settings',
        label: t('nav.settings'),
        keywords: 'settings preferences profile bank',
        section: 'navigate',
        perform: () => void navigate({ to: '/settings' }),
      },
      {
        id: 'create-transaction',
        label: 'New transaction',
        hint: 'Opens Transactions',
        keywords: 'new transaction add cash entry manual',
        section: 'create',
        // Cross-screen: nav to /transactions, then the page's "+
        // New" button can be re-clicked. A global event bus to
        // auto-open the dialog is a follow-up — feels premature for
        // one action.
        perform: () => void navigate({ to: '/transactions' }),
      },
      {
        id: 'create-bank',
        label: 'Link a bank',
        hint: 'Opens Accounts',
        keywords: 'add bank link plaid connect institution',
        section: 'create',
        perform: () => void navigate({ to: '/accounts' }),
      },
      {
        id: 'theme-cycle',
        label: `Theme: ${mode} → ${nextTheme}`,
        keywords: 'theme dark light system mode',
        section: 'preferences',
        perform: () => setMode(nextTheme),
      },
      {
        id: 'sign-out',
        label: t('auth.signOut'),
        keywords: 'sign out logout exit',
        section: 'account',
        perform: () => {
          void signOut()
        },
      },
    ]
  }, [mode, setMode, navigate, t, signOut])

  const filtered = useMemo(() => {
    if (!filter) return actions
    const tokens = filter.toLowerCase().split(/\s+/).filter(Boolean)
    return actions.filter((a) => {
      const haystack = `${a.label} ${a.keywords}`.toLowerCase()
      return tokens.every((tok) => haystack.includes(tok))
    })
  }, [filter, actions])

  // Reset highlight when the filtered list changes; clamp to length.
  useEffect(() => {
    setHighlight(0)
  }, [filter])

  useEffect(() => {
    if (open) {
      // Defer focus so the dialog has rendered.
      setTimeout(() => inputRef.current?.focus(), 0)
    }
  }, [open])

  // Keep the highlighted item in view when arrowing past the
  // scrollable region.
  useEffect(() => {
    if (!open) return
    const list = listRef.current
    if (!list) return
    const item = list.querySelector<HTMLElement>(`[data-action-index="${highlight}"]`)
    item?.scrollIntoView({ block: 'nearest' })
  }, [highlight, open])

  if (!open) return null

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      e.preventDefault()
      close()
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlight((h) => Math.min(h + 1, filtered.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight((h) => Math.max(h - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const target = filtered[highlight]
      if (target) {
        close()
        target.perform()
      }
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 pt-[10vh]"
      onClick={(e) => {
        if (e.target === e.currentTarget) close()
      }}
    >
      <div
        className="w-full max-w-xl overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-2xl dark:border-zinc-700 dark:bg-zinc-900"
        onKeyDown={onKeyDown}
      >
        <input
          ref={inputRef}
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Type a command or search…"
          aria-label="Command"
          className="w-full border-b border-zinc-200 bg-transparent px-4 py-3 text-sm placeholder:text-zinc-400 focus:outline-none dark:border-zinc-700"
        />

        <div ref={listRef} className="max-h-[60vh] overflow-auto p-1" role="listbox">
          {filtered.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-zinc-500">No matches</div>
          ) : (
            renderSections(filtered, highlight, (a) => {
              close()
              a.perform()
            })
          )}
        </div>

        <footer className="flex items-center justify-between border-t border-zinc-200 px-3 py-2 text-xs text-zinc-500 dark:border-zinc-700">
          <div className="flex items-center gap-2">
            <kbd className="rounded bg-zinc-100 px-1.5 py-0.5 dark:bg-zinc-800">↑↓</kbd>
            <span>navigate</span>
            <kbd className="rounded bg-zinc-100 px-1.5 py-0.5 dark:bg-zinc-800">↵</kbd>
            <span>select</span>
            <kbd className="rounded bg-zinc-100 px-1.5 py-0.5 dark:bg-zinc-800">esc</kbd>
            <span>close</span>
          </div>
          <span>
            <kbd className="rounded bg-zinc-100 px-1.5 py-0.5 dark:bg-zinc-800">⌘K</kbd>
          </span>
        </footer>
      </div>
    </div>
  )
}

const SECTION_LABELS: Record<Action['section'], string> = {
  navigate: 'Navigate',
  create: 'Create',
  preferences: 'Preferences',
  account: 'Account',
}

function renderSections(actions: Action[], highlight: number, perform: (a: Action) => void) {
  const bySection = new Map<Action['section'], Action[]>()
  for (const a of actions) {
    const list = bySection.get(a.section) ?? []
    list.push(a)
    bySection.set(a.section, list)
  }
  let runningIdx = 0
  const sections: React.ReactNode[] = []
  for (const sec of ['navigate', 'create', 'preferences', 'account'] as const) {
    const list = bySection.get(sec)
    if (!list || list.length === 0) continue
    sections.push(
      <div key={sec} className="py-1">
        <div className="px-3 pb-1 pt-2 text-xs font-medium uppercase tracking-wide text-zinc-500">
          {SECTION_LABELS[sec]}
        </div>
        {list.map((a) => {
          const idx = runningIdx++
          const active = idx === highlight
          return (
            <button
              key={a.id}
              type="button"
              role="option"
              aria-selected={active}
              data-action-index={idx}
              onClick={() => perform(a)}
              className={`flex w-full items-center justify-between gap-3 rounded px-3 py-2 text-left text-sm ${
                active ? 'bg-blue-50 dark:bg-blue-950/40' : 'hover:bg-zinc-100 dark:hover:bg-zinc-800'
              }`}
            >
              <span>{a.label}</span>
              {a.hint && <span className="text-xs text-zinc-500">{a.hint}</span>}
            </button>
          )
        })}
      </div>,
    )
  }
  return sections
}
